/**
 * The backend's HTTP API (docs/NEW-UI/protocollo.md §2), same origin through the Vite proxy.
 * Every response is checked; a failure is an Error with the status and the backend's own message.
 */
import { z } from 'zod';
import type { BackendApi, RuntimeInfo } from '../core/ports';
import { parseSession, type Session } from '../core/wire';

export class HttpError extends Error {
  constructor(readonly status: number, readonly path: string, detail: string) {
    super(`${path} → ${status}${detail ? `: ${detail}` : ''}`);
  }
}

const accepted = z.object({ turn_id: z.string() });
const queued = z.object({ queued: z.number(), delivery: z.enum(['queued', 'waking', 'screen', 'nobody']).optional() });
const withSession = z.object({ session: z.unknown() });

export function createHttpApi(base = ''): BackendApi {
  async function call(path: string, init?: { body?: unknown }): Promise<unknown> {
    const res = await fetch(`${base}${path}`, init?.body === undefined
      ? undefined
      : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(init.body) });
    const text = await res.text();
    const json = parseJson(text);
    if (!res.ok) throw new HttpError(res.status, path, messageOf(json) ?? text.slice(0, 200));
    return json;
  }

  const session = (data: unknown, path: string): Session => {
    const wrapped = withSession.safeParse(data);
    const parsed = parseSession(wrapped.success ? wrapped.data.session : data);
    if (!parsed.ok) throw new HttpError(200, path, `unexpected session: ${parsed.reason}`);
    return parsed.session;
  };

  const turn = (data: unknown, path: string) => {
    const r = accepted.safeParse(data);
    if (!r.success) throw new HttpError(202, path, 'no turn_id in the response');
    return { turnId: r.data.turn_id };
  };

  return {
    async resetSession(sessionId) {
      return session(await call('/api/session/reset', { body: { session_id: sessionId } }), '/api/session/reset');
    },
    async getSession(sessionId) {
      try {
        return session(await call(`/api/session/${encodeURIComponent(sessionId)}`), '/api/session');
      } catch (e) {
        if (e instanceof HttpError && e.status === 404) return null;
        throw e;
      }
    },
    async press(sessionId, button, data) {
      return turn(await call('/api/v2/action', { body: { session_id: sessionId, button_id: button, data } }), '/api/v2/action');
    },
    async say(sessionId, text) {
      return turn(await call('/api/v2/text', { body: { session_id: sessionId, text } }), '/api/v2/text');
    },
    async setLanguage(sessionId, language) {
      return session(await call('/api/language', { body: { session_id: sessionId, language } }), '/api/language');
    },
    async heartbeat(sessionId) {
      await call('/api/heartbeat', { body: { session_id: sessionId } });
    },
    async scanQr(sessionId, qrData) {
      return turn(await call('/api/v2/vision/qr', { body: { session_id: sessionId, qr_data: qrData } }), '/api/v2/vision/qr');
    },
    async announce(sessionId, text, options) {
      const r = queued.safeParse(await call('/api/v2/announce', { body: { session_id: sessionId, text, ...options } }));
      if (!r.success) throw new HttpError(200, '/api/v2/announce', 'no queued count in the response');
      // a backend before `delivery` only knew open voices
      return { queued: r.data.queued, delivery: r.data.delivery ?? (r.data.queued > 0 ? 'queued' : 'nobody') };
    },
    async ui() {
      try {
        return await call('/api/v2/ui');
      } catch (e) {
        if (e instanceof HttpError && e.status === 404) return null; // a backend before the spec's `ui:`
        throw e;
      }
    },
    async runtime() {
      try {
        const data = (await call('/api/runtime')) as Record<string, unknown> | null;
        if (!data || typeof data.domain !== 'string') return null;
        return data as RuntimeInfo;
      } catch (e) {
        if (e instanceof HttpError && e.status === 404) return null;
        throw e;
      }
    },
  };
}

function parseJson(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null; // a non-JSON body: the status and the raw text say what happened
  }
}

function messageOf(json: unknown): string | undefined {
  if (!json || typeof json !== 'object') return undefined;
  const o = json as Record<string, unknown>;
  if (typeof o.message === 'string') return o.message;
  if (typeof o.detail === 'string') return o.detail;
  if (typeof o.error === 'string') return o.error;
  return undefined;
}
